import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const styles = {
  card: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  title: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#111827',
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: '13px',
    color: '#6b7280',
    margin: '0 0 20px 0',
  },
  svgContainer: {
    width: '100%',
    overflow: 'visible',
  },
  legend: {
    display: 'flex',
    gap: '20px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#374151',
  },
  legendSwatch: {
    width: '12px',
    height: '12px',
    borderRadius: '2px',
    flexShrink: 0,
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#1f2937',
    color: '#f9fafb',
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '12px',
    lineHeight: '1.5',
    zIndex: 9999,
    maxWidth: '280px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#9ca3af',
    fontSize: '14px',
  },
};

const COLORS = {
  national: '#2563eb',
  state: '#f59e0b',
  historical: '#16a34a',
};

const BENCHMARK_LABELS = {
  national: 'National Mean',
  state: 'State Mean',
  historical: 'Historical Mean',
};

const BENCHMARK_KEYS = ['national', 'state', 'historical'];
const DEVIATION_FIELDS = ['fromNational', 'fromState', 'fromHistorical'];
const MAX_ADDRESSES = 20;
const ALERT_THRESHOLD = 5;

export default function DeviationFromBenchmarkChart({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const sortedAddresses = useMemo(() => {
    if (!data || !data.addresses || data.addresses.length === 0) return [];

    const withMaxDev = data.addresses.map((addr) => {
      const devs = addr.deviations || {};
      const absMax = Math.max(
        Math.abs(devs.fromNational || 0),
        Math.abs(devs.fromState || 0),
        Math.abs(devs.fromHistorical || 0)
      );
      return { ...addr, absMaxDeviation: absMax };
    });

    withMaxDev.sort((a, b) => b.absMaxDeviation - a.absMaxDeviation);

    return withMaxDev.slice(0, MAX_ADDRESSES);
  }, [data]);

  useEffect(() => {
    if (!svgRef.current || sortedAddresses.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 700;
    const height = 400;
    const margin = { top: 20, right: 30, bottom: 30, left: 130 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    svg.attr('viewBox', `0 0 ${width} ${height}`);

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    const addressCount = sortedAddresses.length;
    const groupHeight = innerHeight / addressCount;
    const barHeight = Math.min(Math.max(groupHeight * 0.22, 3), 10);
    const barGap = Math.max(barHeight * 0.25, 1);

    const allDeviations = sortedAddresses.flatMap((addr) => {
      const d = addr.deviations || {};
      return [d.fromNational || 0, d.fromState || 0, d.fromHistorical || 0];
    });

    const maxAbsDev = Math.max(d3.max(allDeviations, (d) => Math.abs(d)) || 10, 5);
    const xPadding = maxAbsDev * 0.15;

    const xScale = d3.scaleLinear()
      .domain([-(maxAbsDev + xPadding), maxAbsDev + xPadding])
      .range([0, innerWidth]);

    const yScale = d3.scaleBand()
      .domain(sortedAddresses.map((a) => a.label))
      .range([0, innerHeight])
      .padding(0.25);

    // Grid lines
    const xTicks = xScale.ticks(8);
    g.selectAll('.grid-line')
      .data(xTicks)
      .enter()
      .append('line')
      .attr('x1', (d) => xScale(d))
      .attr('x2', (d) => xScale(d))
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Zero reference line
    g.append('line')
      .attr('x1', xScale(0))
      .attr('x2', xScale(0))
      .attr('y1', -5)
      .attr('y2', innerHeight + 5)
      .attr('stroke', '#374151')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '4,3');

    // Zero label
    g.append('text')
      .attr('x', xScale(0))
      .attr('y', -8)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#374151')
      .attr('font-weight', 600)
      .text('0%');

    // Draw bars for each address
    sortedAddresses.forEach((addr) => {
      const devs = addr.deviations || {};
      const deviationValues = [
        devs.fromNational || 0,
        devs.fromState || 0,
        devs.fromHistorical || 0,
      ];

      const bandCenter = yScale(addr.label) + yScale.bandwidth() / 2;
      const totalBarsHeight = BENCHMARK_KEYS.length * barHeight + (BENCHMARK_KEYS.length - 1) * barGap;
      const startY = bandCenter - totalBarsHeight / 2;

      BENCHMARK_KEYS.forEach((key, i) => {
        const value = deviationValues[i];
        const y = startY + i * (barHeight + barGap);
        const x0 = xScale(0);
        const x1 = xScale(value);
        const barX = Math.min(x0, x1);
        const barW = Math.abs(x1 - x0);

        g.append('rect')
          .attr('x', barX)
          .attr('y', y)
          .attr('width', Math.max(barW, 0.5))
          .attr('height', barHeight)
          .attr('fill', COLORS[key])
          .attr('rx', 2)
          .attr('opacity', 0.85)
          .style('cursor', 'pointer')
          .on('mouseenter', (event) => {
            d3.select(event.target).attr('opacity', 1);
            const alertsList = (addr.alerts && addr.alerts.length > 0)
              ? addr.alerts.join(', ')
              : 'None';
            setTooltip({
              x: event.clientX + 12,
              y: event.clientY - 10,
              content: {
                label: addr.label,
                currentPrice: addr.currentPrice,
                fromNational: devs.fromNational,
                fromState: devs.fromState,
                fromHistorical: devs.fromHistorical,
                alerts: alertsList,
              },
            });
          })
          .on('mousemove', (event) => {
            setTooltip((prev) => prev ? { ...prev, x: event.clientX + 12, y: event.clientY - 10 } : null);
          })
          .on('mouseleave', (event) => {
            d3.select(event.target).attr('opacity', 0.85);
            setTooltip(null);
          });

        // Value label on bar if wide enough
        if (barW > 28) {
          g.append('text')
            .attr('x', value >= 0 ? x1 - 4 : x1 + 4)
            .attr('y', y + barHeight / 2)
            .attr('dy', '0.35em')
            .attr('text-anchor', value >= 0 ? 'end' : 'start')
            .attr('font-size', '9px')
            .attr('fill', '#ffffff')
            .attr('font-weight', 600)
            .attr('pointer-events', 'none')
            .text(`${value > 0 ? '+' : ''}${value.toFixed(1)}%`);
        }
      });

      // Traffic-light indicator dot if any deviation exceeds threshold
      const hasAlert = deviationValues.some((v) => Math.abs(v) > ALERT_THRESHOLD);
      if (hasAlert) {
        const maxDev = deviationValues.reduce((a, b) => Math.abs(a) > Math.abs(b) ? a : b, 0);
        const dotColor = maxDev > 0 ? '#ef4444' : '#f59e0b';
        g.append('circle')
          .attr('cx', -10)
          .attr('cy', bandCenter)
          .attr('r', 4)
          .attr('fill', dotColor)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 1);
      }
    });

    // Y axis (address labels)
    const yAxis = g.append('g')
      .call(d3.axisLeft(yScale).tickSize(0).tickPadding(14));

    yAxis.select('.domain').remove();

    yAxis.selectAll('text')
      .attr('font-size', '11px')
      .attr('fill', '#374151')
      .each(function () {
        const text = d3.select(this);
        const label = text.text();
        if (label.length > 18) {
          text.text(label.substring(0, 16) + '...');
        }
      });

    // X axis
    const xAxis = g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(
        d3.axisBottom(xScale)
          .ticks(8)
          .tickFormat((d) => `${d > 0 ? '+' : ''}${d}%`)
      );

    xAxis.select('.domain').attr('stroke', '#d1d5db');
    xAxis.selectAll('.tick line').attr('stroke', '#d1d5db');
    xAxis.selectAll('.tick text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

  }, [sortedAddresses]);

  if (!data || !data.addresses || data.addresses.length === 0) {
    return (
      <div style={styles.card}>
        <h3 style={styles.title}>Multi-Benchmark Deviation</h3>
        <p style={styles.subtitle}>Address prices vs national, state, and historical benchmarks</p>
        <div style={styles.emptyState}>No deviation data available.</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <h3 style={styles.title}>Multi-Benchmark Deviation</h3>
      <p style={styles.subtitle}>Address prices vs national, state, and historical benchmarks</p>

      <div style={styles.legend}>
        {BENCHMARK_KEYS.map((key) => (
          <div key={key} style={styles.legendItem}>
            <span style={{ ...styles.legendSwatch, backgroundColor: COLORS[key] }} />
            <span>{BENCHMARK_LABELS[key]}</span>
          </div>
        ))}
        <div style={styles.legendItem}>
          <span
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#ef4444',
              border: '1px solid #ffffff',
              boxShadow: '0 0 0 1px #d1d5db',
              flexShrink: 0,
            }}
          />
          <span>Exceeds {ALERT_THRESHOLD}% threshold</span>
        </div>
      </div>

      <div style={styles.svgContainer}>
        <svg
          ref={svgRef}
          style={{ width: '100%', height: 'auto', display: 'block' }}
          preserveAspectRatio="xMidYMid meet"
        />
      </div>

      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: '6px', fontSize: '13px' }}>
            {tooltip.content.label}
          </div>
          <div style={{ marginBottom: '4px' }}>
            Current Price: <strong>${tooltip.content.currentPrice != null ? tooltip.content.currentPrice.toFixed(2) : 'N/A'}</strong>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: COLORS.national, display: 'inline-block' }} />
            vs National: <strong>{formatDeviation(tooltip.content.fromNational)}</strong>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: COLORS.state, display: 'inline-block' }} />
            vs State: <strong>{formatDeviation(tooltip.content.fromState)}</strong>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: COLORS.historical, display: 'inline-block' }} />
            vs Historical: <strong>{formatDeviation(tooltip.content.fromHistorical)}</strong>
          </div>
          <div style={{ borderTop: '1px solid #4b5563', paddingTop: '4px', color: '#d1d5db', fontSize: '11px' }}>
            Alerts: {tooltip.content.alerts}
          </div>
        </div>
      )}
    </div>
  );
}

function formatDeviation(value) {
  if (value == null) return 'N/A';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}
